import { Checkbox, Table } from 'antd'
import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { readRevenueDays } from 'src/utils/revenueCategoryApi'
import {
  attachPreviousPeriod,
  compareRevenueCategories,
  previousPeriodRange,
  salesSplitHeadline,
  summariseRevenueSplit,
} from 'src/utils/revenueSplit'

const money = value =>
  `£${Number(value || 0).toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

const percent = share => `${(Number(share || 0) * 100).toFixed(1)}%`

const periodLabel = (from, to) => {
  const start = dayjs(from)
  const end = dayjs(to)
  if (!start.isValid() || !end.isValid()) return ''
  if (start.isSame(end, 'day')) return start.format('D MMM')
  if (start.isSame(end, 'month'))
    return `${start.format('D')}–${end.format('D MMM')}`
  if (start.isSame(end, 'year')) {
    return `${start.format('D MMM')} – ${end.format('D MMM')}`
  }
  return `${start.format('D MMM YYYY')} – ${end.format('D MMM YYYY')}`
}

const SalesSplit = ({ venueId, from, to }) => {
  const [summary, setSummary] = useState(null)
  const [previous, setPrevious] = useState(null)
  const [days, setDays] = useState([])
  const [selected, setSelected] = useState([])
  const [open, setOpen] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!venueId || !from || !to) return undefined
    let cancelled = false
    setError('')
    setSelected([])
    const earlier = previousPeriodRange(from, to)
    Promise.all([
      readRevenueDays(venueId, from, to),
      earlier
        ? readRevenueDays(venueId, earlier.from, earlier.to)
        : Promise.resolve({ days: [] }),
    ])
      .then(([data, prior]) => {
        if (cancelled) return null
        const loaded = data?.days || []
        setDays(loaded)
        setSummary(summariseRevenueSplit(loaded))
        setPrevious(summariseRevenueSplit(prior?.days || []))
        return null
      })
      .catch(() => {
        if (!cancelled) setError('The sales split could not be loaded.')
      })
    return () => {
      cancelled = true
    }
  }, [venueId, from, to])

  const toggle = name => {
    setSelected(current =>
      current.includes(name)
        ? current.filter(item => item !== name)
        : [...current, name],
    )
  }

  const comparison =
    selected.length >= 2 ? compareRevenueCategories(days, selected) : []
  const headline = salesSplitHeadline(summary?.rows)
  const compared = summary ? attachPreviousPeriod(summary.rows, previous) : null
  const earlier = previousPeriodRange(from, to)
  const earlierLabel = earlier ? periodLabel(earlier.from, earlier.to) : ''
  const thisPeriodLabel = from && to ? periodLabel(from, to) : ''
  const periodCell = (amount, share) => `${money(amount)} · ${percent(share)}`

  return (
    <section
      aria-label='Sales split'
      style={{
        background: '#ffffff',
        border: '1px solid rgba(0, 0, 0, 0.06)',
        borderRadius: 12,
        padding: 24,
        margin: '0 0 24px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
      }}
    >
      <h2 style={{ fontSize: 18, marginBottom: open ? 8 : 0 }}>
        <button
          type='button'
          aria-expanded={open}
          onClick={() => setOpen(current => !current)}
          style={{
            background: 'none',
            border: 0,
            padding: 0,
            font: 'inherit',
            cursor: 'pointer',
            color: 'inherit',
          }}
        >
          {open ? '▾' : '▸'} Sales split
        </button>
        {!open && headline ? (
          <span
            style={{
              marginLeft: 12,
              fontSize: 14,
              fontWeight: 400,
              color: 'rgba(0,0,0,0.65)',
            }}
          >
            {`${headline.name} ${money(headline.total)} · ${percent(
              headline.share,
            )} of ${thisPeriodLabel || 'this period'}`}
          </span>
        ) : null}
      </h2>
      {open ? (
        <>
          <p style={{ margin: '0 0 12px', color: '#555' }}>
            Category totals for the dates selected above. The percent is the
            share of the whole period. Select two or more categories to compare
            them across these days.
          </p>
          {error ? <p>{error}</p> : null}
          {summary == null ? (
            <p>Loading this range…</p>
          ) : summary.rows.length === 0 ? (
            <p>No sales in these dates.</p>
          ) : (
            <>
              {compared?.empty ? (
                <p style={{ margin: '0 0 12px', color: 'rgba(0,0,0,0.45)' }}>
                  {earlierLabel
                    ? `No sales from ${earlierLabel}.`
                    : 'No sales in the previous period.'}
                </p>
              ) : null}
              <Table
                rowKey='name'
                pagination={false}
                dataSource={compared?.rows || summary.rows}
                columns={[
                  {
                    title: 'Category',
                    dataIndex: 'name',
                    key: 'name',
                    render: (name, row) =>
                      row.oneTotal ? (
                        name
                      ) : (
                        <Checkbox
                          checked={selected.includes(name)}
                          onChange={() => toggle(name)}
                        >
                          {name}
                        </Checkbox>
                      ),
                  },
                  {
                    title: thisPeriodLabel
                      ? `This period · ${thisPeriodLabel}`
                      : 'This period',
                    key: 'total',
                    render: (_, row) => periodCell(row.total, row.share),
                  },
                  ...(compared?.empty
                    ? []
                    : [
                        {
                          title: earlierLabel
                            ? `Last period · ${earlierLabel}`
                            : 'Last period',
                          key: 'previous',
                          render: (_, row) =>
                            periodCell(row.previousTotal, row.previousShare),
                        },
                      ]),
                  {
                    title: 'Days entered · this period',
                    dataIndex: 'days',
                    key: 'days',
                    render: daysEntered =>
                      `${daysEntered} ${daysEntered === 1 ? 'day' : 'days'}`,
                  },
                ]}
              />
            </>
          )}
          {comparison.length > 0 ? (
            <div style={{ marginTop: 16 }}>
              <h3 style={{ fontSize: 16, margin: '0 0 4px' }}>
                Across these days
              </h3>
              <p style={{ margin: '0 0 8px', color: '#555' }}>
                The percent here is the share of that day.
              </p>
              <Table
                rowKey='date'
                pagination={false}
                dataSource={comparison}
                scroll={{ x: 'max-content' }}
                columns={[
                  {
                    title: 'Date',
                    dataIndex: 'date',
                    key: 'date',
                    render: (date, row) =>
                      row.oneTotal
                        ? `${dayjs(date).format(
                            'ddd D MMM',
                          )} · One total ${money(row.dayRevenue)}`
                        : dayjs(date).format('ddd D MMM'),
                  },
                  ...selected.map(name => ({
                    title: name,
                    key: name,
                    render: (_, row) => {
                      if (row.oneTotal) return ''
                      const cell = row.cells[name]
                      if (!cell) return ''
                      return `${money(cell.amount)} · ${percent(cell.share)}`
                    },
                  })),
                  ...(comparison.some(row => (row.rest || []).length)
                    ? [
                        {
                          title: 'Rest of the day',
                          key: 'rest',
                          render: (_, row) =>
                            (row.rest || [])
                              .map(
                                part =>
                                  `${part.name} ${money(
                                    part.amount,
                                  )} · ${percent(part.share)}`,
                              )
                              .join(', '),
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  )
}

export default SalesSplit
